import { useEffect, useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useOnboarding } from '../../contexts/OnboardingContext';

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TourTooltipProps {
  targetRect: TargetRect | null;
}

export function TourTooltip({ targetRect }: TourTooltipProps) {
  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    currentTour,
    nextStep,
    prevStep,
    skipTour,
  } = useOnboarding();

  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);

  // Calculate tooltip position - all coordinates are viewport-relative (fixed positioning)
  useEffect(() => {
    if (!tooltipRef.current || !currentStep) {
      setIsPositioned(false);
      return;
    }

    // Use requestAnimationFrame to ensure DOM is ready
    const calculatePosition = () => {
      const tooltip = tooltipRef.current;
      if (!tooltip) return;

      const tooltipRect = tooltip.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const gap = 16;
      let top = 0;
      let left = 0;

      if (!targetRect) {
        // Center tooltip if no target
        top = viewportHeight / 2 - tooltipRect.height / 2;
        left = viewportWidth / 2 - tooltipRect.width / 2;
      } else {
        const placement = currentStep.placement;

        switch (placement) {
          case 'top':
            top = targetRect.top - tooltipRect.height - gap;
            left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
            break;
          case 'bottom':
            top = targetRect.top + targetRect.height + gap;
            left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
            break;
          case 'left':
            top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
            left = targetRect.left - tooltipRect.width - gap;
            break;
          case 'right':
            top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
            left = targetRect.left + targetRect.width + gap;
            break;
        }

        // Keep tooltip in viewport with smart repositioning
        const minPadding = 16;

        // Horizontal bounds
        if (left < minPadding) {
          left = minPadding;
        } else if (left + tooltipRect.width > viewportWidth - minPadding) {
          left = viewportWidth - tooltipRect.width - minPadding;
        }

        // Vertical bounds - flip placement if needed
        if (top < minPadding) {
          // If tooltip would go above viewport, place it below target instead
          if (placement === 'top' && targetRect) {
            top = targetRect.top + targetRect.height + gap;
          } else {
            top = minPadding;
          }
        } else if (top + tooltipRect.height > viewportHeight - minPadding) {
          // If tooltip would go below viewport, place it above target instead
          if (placement === 'bottom' && targetRect) {
            top = targetRect.top - tooltipRect.height - gap;
            // If still doesn't fit, just clamp to bottom
            if (top < minPadding) {
              top = viewportHeight - tooltipRect.height - minPadding;
            }
          } else {
            top = viewportHeight - tooltipRect.height - minPadding;
          }
        }

        // Final safety clamp
        top = Math.max(minPadding, Math.min(top, viewportHeight - tooltipRect.height - minPadding));
        left = Math.max(minPadding, Math.min(left, viewportWidth - tooltipRect.width - minPadding));
      }

      setPosition({ top, left });
      setIsPositioned(true);
    };

    // Small delay to ensure tooltip is rendered and measurable
    const timer = setTimeout(() => {
      requestAnimationFrame(calculatePosition);
    }, 50);

    return () => clearTimeout(timer);
  }, [targetRect, currentStep]);

  if (!currentStep || !currentTour) return null;

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;

  return (
    <div
      ref={tooltipRef}
      className="fixed bg-white rounded-2xl shadow-2xl p-5 max-w-sm pointer-events-auto animate-in fade-in slide-in-from-bottom-2 duration-300"
      style={{
        top: position.top,
        left: position.left,
        zIndex: 10000,
        opacity: isPositioned ? 1 : 0,
        transition: 'opacity 0.2s ease-in-out',
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <p className="text-xs font-medium text-primary mb-1">
            {currentTour.name}
          </p>
          <h3 className="text-lg font-semibold text-gray-900">
            {currentStep.title}
          </h3>
        </div>
        <button
          onClick={skipTour}
          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          title="Tour beenden"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        {currentStep.content}
      </p>

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-1.5 mb-4">
        {Array.from({ length: totalSteps }).map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-colors ${
              index === currentStepIndex
                ? 'bg-primary'
                : index < currentStepIndex
                ? 'bg-primary/40'
                : 'bg-gray-200'
            }`}
          />
        ))}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <button
          onClick={skipTour}
          className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
        >
          Überspringen
        </button>

        <div className="flex items-center gap-2">
          {!isFirstStep && (
            <button
              onClick={prevStep}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Zurück
            </button>
          )}
          <button
            onClick={nextStep}
            className="flex items-center gap-1 px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
          >
            {isLastStep ? 'Fertig' : 'Weiter'}
            {!isLastStep && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Step counter */}
      <p className="text-xs text-gray-400 text-center mt-3">
        Schritt {currentStepIndex + 1} von {totalSteps}
      </p>
    </div>
  );
}
